import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { 
  ArrowRight, Radio, 
  Mic, Video, Tv, Globe, Volume2, VolumeX, Play 
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import styles from './Hero.module.css';

const Hero = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { t, isMuted, setIsMuted, hasInteracted, setHasInteracted } = useLanguage();
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  // --- 1. LIGHTSPEED ASSET OPTIMIZATION ---
  // Using Cloudinary's f_auto (auto format) and q_auto (auto quality)
  // Also forcing a smaller width for the square mobile-style frame
  const optimizeUrl = (url) => {
    return url.replace('/upload/', '/upload/f_auto,q_auto,w_600,c_limit/');
  };

  const videos = useMemo(() => [
    { url: optimizeUrl("https://res.cloudinary.com/ddkyeuhk8/video/upload/v1773915196/a0busojxf6ake5clxfxn.webm"), label: "National" },
    { url: optimizeUrl("https://res.cloudinary.com/ddkyeuhk8/video/upload/v1773915319/tgmvb9oo0b7rqwxfthsz.webm"), label: "Breaking" },
    { url: optimizeUrl("https://res.cloudinary.com/ddkyeuhk8/video/upload/v1773918126/ri0cndrudecuoee5v2et.webm"), label: "Global" }, 
    { url: optimizeUrl("https://res.cloudinary.com/ddkyeuhk8/video/upload/v1773918138/s7zvfknnqqqbrgirhbns.webm"), label: "Corporate" },
    { url: optimizeUrl("https://res.cloudinary.com/ddkyeuhk8/video/upload/v1773915454/fjqubtwsm4z0vbylumq4.webm"), label: "Special" },
  ], []);

  // --- 2. NEXT-VIDEO PRELOADING (Instant Switch) ---
  useEffect(() => {
    const nextIndex = (activeIndex + 1) % videos.length;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'video';
    link.href = videos[nextIndex].url;
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, [activeIndex, videos]);

  // --- 3. SCROLL-TO-PAUSE (Optimized Observer) ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!videoRef.current) return;
        entry.isIntersecting ? (hasInteracted && videoRef.current.play()) : videoRef.current.pause();
      },
      { threshold: 0.1 } 
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [hasInteracted]);

  // --- 4. VOICE POLICY ---
  useEffect(() => {
    const enableVoice = () => {
      setIsMuted(false);
      setHasInteracted(true);
      window.removeEventListener('click', enableVoice);
    };
    window.addEventListener('click', enableVoice);
    return () => window.removeEventListener('click', enableVoice);
  }, [setIsMuted, setHasInteracted]);

  // --- 5. PARALLAX (GPU Optimized) ---
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 60, damping: 25 });
  const smoothY = useSpring(mouseY, { stiffness: 60, damping: 25 });

  const rotateX = useTransform(smoothY, [-0.5, 0.5], [10, -10]);
  const rotateY = useTransform(smoothX, [-0.5, 0.5], [-10, 10]);
  const bgX = useTransform(smoothX, [-0.5, 0.5], [30, -30]);
  const bgY = useTransform(smoothY, [-0.5, 0.5], [30, -30]);

  const handleMouseMove = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section 
      ref={containerRef} 
      className={styles.heroContainer} 
      onMouseMove={handleMouseMove}
    >
      <div className={styles.bgInteractiveLayer}>
        <motion.div style={{ x: bgX, y: bgY }} className={styles.iconCloud}>
          <Mic className={styles.bgIcon} style={{ top: '15%', left: '10%' }} size={100} />
          <Video className={styles.bgIcon} style={{ top: '60%', left: '5%' }} size={120} />
          <Tv className={styles.bgIcon} style={{ top: '70%', right: '10%' }} size={130} />
          <Globe className={styles.bgIcon} style={{ top: '40%', right: '35%' }} size={70} />
        </motion.div>
        <div className={styles.blobOrange} />
        <div className={styles.blobBlue} />
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.textContent}>
          <h1 className={styles.mainTitle}>{t('hero_title_prefix')}</h1>
          <h2 className={styles.hindiHeadline} style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', color: '#5ba5e0' }}>{t('hero_title_suffix')}</h2>

          <div className={styles.descriptionBox}>
            <p className={styles.heroSubText}>{t('hero_tagline')}</p>
            <p className={styles.heroHindiDetail}>{t('hero_extra')}</p>
          </div>

          <div className={styles.breadcrumbWrapper}>
            {videos.map((_, idx) => (
              <div key={idx} className={`${styles.crumb} ${idx === activeIndex ? styles.activeCrumb : ''}`} onClick={() => setActiveIndex(idx)}>
                <div className={styles.crumbBar}>
                  {idx === activeIndex && <motion.div layoutId="progress" className={styles.progressFill} transition={{ duration: 0.3 }} />}
                </div>
                <span className={styles.crumbLabel}>0{idx + 1}</span>
              </div>
            ))}
          </div>

          <div className={styles.heroButtons}>
            <button className={styles.btnPrimary} onClick={() => document.getElementById('samples')?.scrollIntoView({ behavior: 'smooth' })}>
              {t('hero_btn_samples')} <Play size={18} fill="currentColor" />
            </button>
            <button className={styles.btnSecondary} onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}>
              {t('hero_btn_pricing')} <ArrowRight size={18} />
            </button>
          </div>
        </div>

        <div className={styles.visualSide}>
          <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className={styles.squareFrame}>
            <AnimatePresence mode="wait">
              <motion.div 
                key={activeIndex} 
                className={styles.videoWrapper} 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <video 
                  ref={videoRef} 
                  src={videos[activeIndex].url} 
                  autoPlay 
                  muted={isMuted} 
                  onEnded={() => setActiveIndex((prev) => (prev + 1) % videos.length)} 
                  className={styles.customVideo} 
                  playsInline 
                  preload="auto"
                />
                
                <div className={styles.overlayUI}>
                  <div className={styles.topRow}>
                    <div className={styles.liveBadge}><Radio size={14} className={styles.pulseIcon} /> LIVE</div>
                    <div className={styles.audioIcon}>{isMuted ? <VolumeX size={18} /> : <Volume2 size={18} color="#e87355" />}</div>
                  </div>
                  <div className={styles.coordinates}>AI_CORE_v4.2</div>
                </div>

                {!hasInteracted && (
                  <div className={styles.voiceOverlay}>
                    <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 2 }} className={styles.playBtn}>
                      <Play fill="white" size={32} />
                    </motion.div>
                    <span>Enable AI Voice / आवाज़ शुरू करें</span>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      <div className={styles.tickerContainer}>
        <div className={styles.tickerTitle}>ANANTA UPDATE</div>
        <div className={styles.tickerTrack}>
          <div className={styles.tickerContent}>
             <span>{t('hero_ticker')}</span>
             <span>{t('hero_ticker')}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;